/**
 * ARVEXIS DREAM CARD
 * ==================
 *
 * One polished image that represents a Dream. Not a document, not a screenshot of the
 * Reflection. It carries the Dream's own artwork, the Dream's title, a concise excerpt drawn
 * from the Reflection the member already has, and restrained Arvexis provenance.
 *
 * The full Reflection stays inside Arvexis.
 *
 * Artifact: 1080 x 1350 (4:5), captured at 2x -> 2160 x 2700.
 * The capture always reads an off-screen, full-size, untransformed, unclipped instance —
 * never the scaled preview.
 */

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import { Check, X, Download, Share2 } from 'lucide-react';

// =============================================================================
// TOKENS — the accepted Dream visual language: deep midnight, restrained champagne, ivory
// =============================================================================
const CARD = {
  bg: '#06070B',
  champagne: '#E8C58F',
  champagneSoft: 'rgba(232,197,143,0.55)',
  champagneHair: 'rgba(232,197,143,0.26)',
  ivory: '#F4EFE7',
  ivoryMuted: 'rgba(244,239,231,0.86)',
  ivoryFaint: 'rgba(244,239,231,0.52)',
};

const FONTS = {
  serif: '"Cormorant Garamond", Georgia, serif',
  sans: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
};

// The Dream surfaces' own backgrounds, unchanged.
const ATMOSPHERE = `
  linear-gradient(180deg, rgba(4,6,11,0.30) 0%, rgba(4,6,11,0.55) 100%),
  url('/B_Approved_background_v2.png') center center / cover no-repeat,
  linear-gradient(180deg, #070A12 0%, #05070E 62%, #04060B 100%)
`;
const DESKTOP_ATMOSPHERE = `
  linear-gradient(180deg, rgba(4,6,11,0.30) 0%, rgba(4,6,11,0.55) 100%),
  url('/Canonical_Desktop_Background_v2.png') center center / cover no-repeat,
  linear-gradient(180deg, #070A12 0%, #04060B 100%)
`;
const DESKTOP_SIDEBAR = 205;

export const CARD_W = 1080;
export const CARD_H = 1350;
export const CARD_SCALE = 2;

// =============================================================================
// THE DREAM'S OWN ARTWORK — never regenerated here
// -----------------------------------------------------------------------------
// The artwork was generated once, when the Dream was recorded (Generate Image V1, via
// dream_artwork_service). The card reads that same image by its id.
// =============================================================================
const artworkSourceUrl = (dream) => {
  if (!dream) return null;
  if (dream.artwork_image_id) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/images/${dream.artwork_image_id}`;
  }
  if (dream.artwork_url) return dream.artwork_url;
  if (dream.generated_artwork_url) return dream.generated_artwork_url;
  return null;
};

/**
 * The artwork is served from our own authenticated route, so it is inlined as a data URL
 * before any capture — an anonymous cross-origin <img> would taint the canvas and the
 * artifact would come back without its artwork.
 */
export const useDreamCardArtwork = (dream) => {
  const [artworkDataUrl, setArtworkDataUrl] = useState(null);

  useEffect(() => {
    const url = artworkSourceUrl(dream);
    if (!url) {
      setArtworkDataUrl(null);
      return undefined;
    }
    let cancelled = false;
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('artwork unavailable'))))
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUrl) => { if (!cancelled) setArtworkDataUrl(dataUrl); })
      .catch(() => { if (!cancelled) setArtworkDataUrl(null); });
    return () => { cancelled = true; };
  }, [dream]);

  return artworkDataUrl;
};

// =============================================================================
// THE EXCERPT — taken from the Reflection that already exists
// -----------------------------------------------------------------------------
// No model call. Nothing is rewritten, summarised or made more shareable. Whole sentences
// are taken in order from the Reflection the member is reading, until the card's space is
// used. The complete Reflection remains only in the Reflection experience.
// =============================================================================
export const EXCERPT_MAX = 260;

export const selectReflectionExcerpt = (dream) => {
  const source = (
    dream?.emotional_summary ||
    dream?.integrated_interpretation ||
    dream?.reflection_question ||
    dream?.grounding_insight ||
    ''
  ).toString().replace(/\s+/g, ' ').trim();

  if (!source) return '';
  if (source.length <= EXCERPT_MAX) return source;

  const sentences = source.match(/[^.!?…]+[.!?…]+|[^.!?…]+$/g) || [source];
  let out = '';
  for (let i = 0; i < sentences.length; i += 1) {
    const candidate = `${out}${sentences[i]}`.replace(/\s+/g, ' ').trim();
    if (candidate.length > EXCERPT_MAX) break;
    out = `${candidate} `;
  }
  out = out.trim();
  if (out) return out;

  // A single sentence longer than the card's space: cut on a word boundary, nothing reworded.
  return `${source.slice(0, EXCERPT_MAX - 1).replace(/\s+\S*$/, '')}…`;
};

// Every field is clamped in JS, not CSS: html2canvas does not resolve CSS line clamping, and
// the captured artifact must be identical in shape to the preview however long the Dream's
// title happens to be.
const clamp = (value, max) => {
  const text = (value || '').toString().replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
};

// =============================================================================
// THE ARTIFACT
// =============================================================================
const ProvenanceMark = () => (
  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden="true">
    <circle cx="17" cy="17" r="16" stroke={CARD.champagneSoft} strokeWidth="1" />
    <path
      d="M11 22c0-6 4.5-10 11-10.5C21.5 18 17.5 22 11 22z"
      stroke={CARD.champagne}
      strokeWidth="1.1"
      fill="none"
    />
    <path d="M11 22L22 11.5" stroke={CARD.champagne} strokeWidth="1.1" />
  </svg>
);

export const DreamCardImage = React.forwardRef(({ dream, lang = 'en', artworkDataUrl }, ref) => {
  const title = clamp(dream?.title, 62) || (lang === 'es' ? 'Un sueño' : 'A dream');
  const excerpt = selectReflectionExcerpt(dream);
  const labels = {
    kicker: lang === 'es' ? 'REFLEXIÓN DEL SUEÑO' : 'DREAM REFLECTION',
    provenance: lang === 'es' ? 'DE MI DIARIO DE SUEÑOS  ·  ARVEXIS' : 'FROM MY DREAM JOURNAL  ·  ARVEXIS',
  };

  return (
    <div
      ref={ref}
      id="dream-card-artifact"
      data-testid="dream-card-artifact"
      style={{
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        backgroundColor: CARD.bg,
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '76px 72px 68px 72px',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: FONTS.sans,
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      }}
    >
      {/* Editorial frame */}
      <div
        style={{
          position: 'absolute',
          top: '32px',
          left: '32px',
          right: '32px',
          bottom: '32px',
          border: `1px solid ${CARD.champagneHair}`,
          borderRadius: '22px',
          pointerEvents: 'none',
        }}
      />

      {/* ARVEXIS / DREAM REFLECTION */}
      <div style={{ position: 'relative', zIndex: 1, marginBottom: '34px' }}>
        <div
          style={{
            fontFamily: FONTS.serif,
            fontSize: '29px',
            fontWeight: 500,
            letterSpacing: '11px',
            color: CARD.champagne,
            lineHeight: 1.1,
          }}
        >
          ARVEXIS
        </div>
        <div
          style={{
            marginTop: '12px',
            fontFamily: FONTS.sans,
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '6.5px',
            color: CARD.ivoryFaint,
            lineHeight: 1.2,
          }}
        >
          {labels.kicker}
        </div>
      </div>

      {/* The Dream's artwork */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '560px',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: 'rgba(244,239,231,0.04)',
        }}
      >
        {artworkDataUrl && (
          <img
            src={artworkDataUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {/* The artwork settles into the card rather than sitting on it */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '190px',
            background: `linear-gradient(180deg, rgba(6,7,11,0) 0%, rgba(6,7,11,0.72) 62%, ${CARD.bg} 100%)`,
          }}
        />
      </div>

      {/* Title */}
      <h1
        style={{
          position: 'relative',
          zIndex: 1,
          margin: '44px 0 0 0',
          fontFamily: FONTS.serif,
          fontSize: '62px',
          fontWeight: 500,
          lineHeight: 1.14,
          color: CARD.champagne,
        }}
      >
        {title}
      </h1>

      {/* Rule */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '74px',
          height: '1px',
          backgroundColor: CARD.champagneSoft,
          margin: '30px 0 28px 0',
        }}
      />

      {/* The excerpt from the existing Reflection */}
      {excerpt && (
        <p
          style={{
            position: 'relative',
            zIndex: 1,
            margin: 0,
            fontFamily: FONTS.serif,
            fontSize: '30px',
            fontWeight: 400,
            lineHeight: 1.6,
            color: CARD.ivoryMuted,
          }}
          data-testid="dream-card-artifact-excerpt"
        >
          {excerpt}
        </p>
      )}

      {/* Provenance */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          marginTop: 'auto',
          paddingTop: '48px',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <ProvenanceMark />
        <span
          style={{
            fontFamily: FONTS.sans,
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '5px',
            color: CARD.ivoryFaint,
            lineHeight: 1.2,
          }}
        >
          {labels.provenance}
        </span>
      </div>
    </div>
  );
});
DreamCardImage.displayName = 'DreamCardImage';

/**
 * Off-screen, full size, no transform, no clipping ancestor. The one capture target.
 * It is portalled to <body> so no scrolling or clipping container of the surface can ever
 * take part in laying out the artifact.
 */
export const DreamCardCaptureSource = React.forwardRef(({ dream, lang, artworkDataUrl }, ref) => (
  createPortal(
    <div
      aria-hidden="true"
      data-testid="dream-card-capture-source"
      style={{
        position: 'fixed',
        top: 0,
        left: `-${CARD_W * 2}px`,
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        pointerEvents: 'none',
        zIndex: -1,
      }}
    >
      <DreamCardImage ref={ref} dream={dream} lang={lang} artworkDataUrl={artworkDataUrl} />
    </div>,
    document.body
  )
));
DreamCardCaptureSource.displayName = 'DreamCardCaptureSource';

/** The one capture call. Returns a PNG blob or throws — never a half-made artifact. */
export const renderDreamCardPngBlob = async (node) => {
  if (!node) throw new Error('dream card capture source is not mounted');
  if (document.fonts?.ready) { try { await document.fonts.ready; } catch (e) { /* ignore */ } }
  await new Promise((resolve) => setTimeout(resolve, 120));
  const canvas = await html2canvas(node, {
    scale: CARD_SCALE,
    useCORS: true,
    allowTaint: true,
    backgroundColor: CARD.bg,
    width: CARD_W,
    height: CARD_H,
    // The capture must not inherit the host page's scroll position or viewport size.
    windowWidth: CARD_W,
    windowHeight: CARD_H,
    scrollX: 0,
    scrollY: 0,
    x: 0,
    y: 0,
    logging: false,
  });
  if (canvas.width !== CARD_W * CARD_SCALE || canvas.height !== CARD_H * CARD_SCALE) {
    throw new Error(`dream card rendered at ${canvas.width}x${canvas.height}`);
  }
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob || blob.size < 1024) throw new Error('dream card render produced no image');
  return blob;
};

const cardFileName = (dream) => {
  const created = dream?.created_at ? new Date(dream.created_at) : new Date();
  const stamp = Number.isNaN(created.getTime())
    ? new Date().toISOString().split('T')[0]
    : created.toISOString().split('T')[0];
  return `arvexis-dream-card-${stamp}.png`;
};

const cardFile = (blob, dream) => new File([blob], cardFileName(dream), { type: 'image/png' });

/**
 * iOS cannot let a web app write into Photos itself, and an <a download> there raises Safari's
 * own View/Download dialog before the file is written anywhere. So on a phone the save goes
 * through the operating system's own save mediation, where the member gets Save Image (Photos)
 * and Save to Files directly. Nothing about that layer is Arvexis'.
 */
const prefersNativeSave = () => {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return isIOS || /Android/.test(ua);
};

/**
 * Save Image — the completed card onto the member's device by the most direct route the
 * platform truthfully supports. Returns what actually happened; it never claims Photos.
 */
export const saveDreamCardImage = async ({ node, dream }) => {
  const blob = await renderDreamCardPngBlob(node);
  const file = cardFile(blob, dream);

  if (prefersNativeSave() && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: dream?.title || 'Arvexis' });
      return 'handed';
    } catch (e) {
      if (e && e.name === 'AbortError') return 'cancelled';
      throw e;
    }
  }

  const anchor = document.createElement('a');
  if (typeof anchor.download !== 'undefined') {
    const url = URL.createObjectURL(blob);
    anchor.href = url;
    anchor.download = cardFileName(dream);
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    return 'downloaded';
  }

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: dream?.title || 'Arvexis' });
      return 'handed';
    } catch (e) {
      if (e && e.name === 'AbortError') return 'cancelled';
      throw e;
    }
  }
  throw new Error('no save mechanism available');
};

/**
 * Share — the completed card into the device's own share flow. The operating system provides
 * the destinations; Arvexis provides none.
 */
export const shareDreamCardImage = async ({ node, dream }) => {
  const blob = await renderDreamCardPngBlob(node);
  const file = cardFile(blob, dream);
  if (!(navigator.canShare && navigator.canShare({ files: [file] }))) {
    const error = new Error('native image sharing is unavailable');
    error.code = 'share-unsupported';
    throw error;
  }
  try {
    await navigator.share({ files: [file], title: dream?.title || 'Arvexis' });
    return 'shared';
  } catch (e) {
    if (e && e.name === 'AbortError') return 'cancelled';
    throw e;
  }
};

// =============================================================================
// THE SURFACE — Dream Card, its preview, Save Image | Share
// =============================================================================
const DreamCard = ({ dream, lang = 'en', onClose }) => {
  const captureRef = useRef(null);
  const artworkDataUrl = useDreamCardArtwork(dream);
  const [busy, setBusy] = useState(null);
  const [successKey, setSuccessKey] = useState(null);
  const [error, setError] = useState(null);
  const [isWide, setIsWide] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsWide(mq.matches);
    const onChange = (e) => setIsWide(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const labels = {
    title: lang === 'es' ? 'Tarjeta del Sueño' : 'Dream Card',
    intro: lang === 'es'
      ? 'Un recuerdo visual de tu Diario de Sueños.'
      : 'A visual memory from your Dream Journal.',
    save: lang === 'es' ? 'Guardar Imagen' : 'Save Image',
    share: lang === 'es' ? 'Compartir' : 'Share',
    working: lang === 'es' ? 'Preparando tu tarjeta…' : 'Preparing your card…',
    close: lang === 'es' ? 'Cerrar' : 'Close',
    okSaved: lang === 'es' ? 'Imagen guardada en tu dispositivo' : 'Image saved to your device',
    okHanded: lang === 'es'
      ? 'Imagen lista — usa Guardar imagen o Guardar en Archivos'
      : 'Image ready — choose Save Image or Save to Files',
    okShared: lang === 'es' ? 'Tarjeta del Sueño compartida' : 'Dream Card shared',
    errRender: lang === 'es'
      ? 'No se pudo crear la imagen. Inténtalo de nuevo.'
      : "Couldn't create the image. Try again.",
    errShare: lang === 'es'
      ? 'Este navegador no puede compartir imágenes. Usa Guardar Imagen.'
      : "This browser can't share images. Use Save Image instead.",
  };

  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    setSuccessKey(null);
    try {
      const outcome = await fn({ node: captureRef.current, dream, lang });
      // Nothing claims a success it did not observe: a cancelled OS sheet says nothing, and a
      // save the operating system mediated is not reported as already sitting in Photos.
      if (outcome === 'cancelled') return;
      setSuccessKey(key === 'save' && outcome === 'handed' ? 'handed' : key);
      setTimeout(() => setSuccessKey(null), 8000);
    } catch (e) {
      setError(e?.code === 'share-unsupported' ? labels.errShare : labels.errRender);
      setTimeout(() => setError(null), 8000);
    } finally {
      setBusy(null);
    }
  };

  const previewWidth = isWide ? 352 : 292;
  const previewScale = previewWidth / CARD_W;

  const successMessage = successKey === 'save' ? labels.okSaved
    : successKey === 'handed' ? labels.okHanded
      : successKey === 'share' ? labels.okShared
        : null;

  const actions = [
    { key: 'save', icon: Download, label: labels.save, primary: true,
      onClick: () => run('save', saveDreamCardImage) },
    { key: 'share', icon: Share2, label: labels.share, primary: false,
      onClick: () => run('share', shareDreamCardImage) },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{
        background: isWide ? DESKTOP_ATMOSPHERE : ATMOSPHERE,
        backgroundAttachment: isWide ? 'fixed' : undefined,
        left: isWide ? DESKTOP_SIDEBAR : 0,
      }}
      data-testid="dream-card-page"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
        style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)' }}
        aria-label={labels.close}
        data-testid="dream-card-close-btn"
      >
        <X size={22} />
      </button>

      <div className="mx-auto w-full px-5 pt-14 pb-16" style={{ maxWidth: 440 }}>
        <div className="flex flex-col items-center text-center">
          <h1
            className="text-white"
            style={{ fontFamily: FONTS.serif, fontSize: isWide ? 38 : 33, lineHeight: 1.12 }}
            data-testid="dream-card-title"
          >
            {labels.title}
          </h1>
          <p
            className="text-[#F4EFE7]/45 text-sm mt-3 max-w-[19rem]"
            data-testid="dream-card-subtitle"
          >
            {labels.intro}
          </p>
        </div>

        {/* Preview — the same component, scaled for the screen only */}
        <div className="mt-9 flex justify-center">
          <div
            className="relative overflow-hidden rounded-xl"
            style={{
              width: previewWidth,
              height: Math.round(previewWidth * (CARD_H / CARD_W)),
              border: '1px solid rgba(232,197,143,0.18)',
            }}
            data-testid="dream-card-preview"
          >
            <div
              style={{
                transform: `scale(${previewScale})`,
                transformOrigin: 'top left',
                width: CARD_W,
                height: CARD_H,
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <DreamCardImage dream={dream} lang={lang} artworkDataUrl={artworkDataUrl} />
            </div>
          </div>
        </div>

        {/* The authoritative capture source */}
        <DreamCardCaptureSource
          ref={captureRef}
          dream={dream}
          lang={lang}
          artworkDataUrl={artworkDataUrl}
        />

        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl mt-7"
              style={{ background: 'rgba(232,197,143,0.10)', border: '1px solid rgba(232,197,143,0.24)' }}
            >
              <Check size={16} style={{ color: CARD.champagne }} />
              <span className="text-sm" style={{ color: CARD.champagne }} data-testid="dream-card-success">
                {successMessage}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div
            className="px-5 py-3 rounded-xl text-center text-sm mt-7"
            style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.28)', color: '#fca5a5' }}
            data-testid="dream-card-error"
          >
            {error}
          </div>
        )}

        {/* Save Image | Share */}
        <div className="mt-8 flex items-stretch gap-3">
          {actions.map(({ key, icon: Icon, label, primary, onClick }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              disabled={busy !== null}
              className="flex-1 flex items-center justify-center gap-2.5 px-5 py-4 rounded-xl text-sm transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
              style={{
                background: primary ? 'rgba(232,197,143,0.08)' : 'rgba(255,255,255,0.035)',
                border: primary
                  ? '1px solid rgba(232,197,143,0.56)'
                  : '1px solid rgba(244,239,231,0.12)',
                color: primary ? CARD.ivory : 'rgba(244,239,231,0.78)',
                cursor: busy !== null ? 'wait' : 'pointer',
              }}
              data-testid={key === 'save' ? 'dream-card-save-image-btn' : 'dream-card-share-btn'}
            >
              <Icon size={16} />
              <span className="font-medium">{label}</span>
            </button>
          ))}
        </div>

        {busy && (
          <p className="text-center text-[#F4EFE7]/40 text-xs mt-5" data-testid="dream-card-working">
            {labels.working}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default DreamCard;
